import { v4 as uuidv4 } from 'uuid';

import {
  generateAudio,
  RateLimitError,
} from '@/infrastructure/elevenlabs/ElevenLabsClient';
import type { Scene, AppSettings } from '@/types';
import type {
  AudioGenerationParams,
  AudioGenerationResult,
  ProgressCallback,
  BatchGenerationCallbacks,
  BatchGenerationResult,
} from '@/types/application';
import type { AudioWorkerOutputMessage } from '@/types/workers';
import { logger } from '@/lib/logger';
import { AUDIO_GENERATION } from '@/lib/constants';
import { getAudioDuration } from '@/lib/audioUtils';
import { isAbortError } from '@/lib/errorUtils';

// 保留中のリクエスト管理
interface PendingRequest {
  sceneId: string;
  resolve: (result: { audioUrl: string; audioDuration: number }) => void;
  reject: (error: Error) => void;
}

export class AudioGenerationService {
  private audioWorker: Worker | null = null;
  private pendingRequests: Map<string, PendingRequest> = new Map();

  /**
   * Workerを初期化する
   */
  private initWorker(): Worker {
    if (!this.audioWorker) {
      this.audioWorker = new Worker(
        new URL('@/workers/audio-generator.worker.ts', import.meta.url),
        { type: 'module' }
      );

      this.audioWorker.onmessage = (event: MessageEvent<AudioWorkerOutputMessage>) => {
        this.handleWorkerMessage(event.data);
      };

      this.audioWorker.onerror = (error) => {
        logger.error('AudioWorkerでエラーが発生しました', new Error(error.message));
        // 全ての保留中リクエストをエラーで解決
        this.pendingRequests.forEach((request) => {
          request.reject(new Error(`Worker error: ${error.message}`));
        });
        this.pendingRequests.clear();
      };
    }
    return this.audioWorker;
  }

  /**
   * Workerからのメッセージを処理する
   */
  private async handleWorkerMessage(message: AudioWorkerOutputMessage): Promise<void> {
    const pending = this.pendingRequests.get(message.requestId);
    if (!pending) {
      logger.warn('不明なrequestIdのメッセージを受信しました', { requestId: message.requestId });
      return;
    }

    this.pendingRequests.delete(message.requestId);

    if (message.type === 'complete') {
      try {
        // ArrayBufferをBlobに変換して音声時間を取得
        const audioBlob = new Blob([message.audioData], { type: 'audio/mpeg' });
        const duration = await getAudioDuration(audioBlob);
        const audioUrl = URL.createObjectURL(audioBlob);

        pending.resolve({ audioUrl, audioDuration: duration });
      } catch (error) {
        logger.error('Worker音声データの処理に失敗しました', error as Error, {
          requestId: message.requestId,
          sceneId: message.sceneId,
        });
        pending.reject(error as Error);
      }
    } else if (message.type === 'error') {
      if (message.errorType === 'abort') {
        pending.reject(new DOMException('Aborted', 'AbortError'));
      } else if (message.errorType === 'rate-limit') {
        pending.reject(new RateLimitError(message.error));
      } else {
        pending.reject(new Error(message.error));
      }
    }
  }

  /**
   * Workerによる単一音声生成
   */
  private generateSingleAudioWithWorker(
    scene: Scene,
    settings: AppSettings
  ): Promise<{ audioUrl: string; audioDuration: number }> {
    return new Promise((resolve, reject) => {
      const worker = this.initWorker();
      const requestId = uuidv4();

      this.pendingRequests.set(requestId, {
        sceneId: scene.id,
        resolve,
        reject,
      });

      worker.postMessage({
        type: 'generate',
        requestId,
        params: {
          sceneId: scene.id,
          text: scene.text,
          voiceId: settings.voiceId,
          apiKey: settings.elevenLabsApiKey,
          stability: settings.voiceStability,
          similarityBoost: settings.voiceSimilarityBoost,
        },
      });
    });
  }

  /**
   * Worker生成をキャンセルする
   */
  cancelWorkerGeneration(): void {
    if (this.audioWorker) {
      logger.info('音声生成Workerをキャンセルしています...');
      this.audioWorker.postMessage({ type: 'abort-all' });
      // 保留中のリクエストをAbortErrorで解決
      this.pendingRequests.forEach((request) => {
        request.reject(new DOMException('Aborted', 'AbortError'));
      });
      this.pendingRequests.clear();
    }
  }

  /**
   * Workerを終了する
   */
  terminateWorker(): void {
    if (this.audioWorker) {
      this.audioWorker.terminate();
      this.audioWorker = null;
      this.pendingRequests.clear();
    }
  }

  async generateSingleAudio(
    scene: Scene,
    settings: AppSettings,
    signal?: AbortSignal
  ): Promise<AudioGenerationResult> {
    logger.info('個別音声生成開始', { sceneId: scene.id });

    const params: AudioGenerationParams = {
      text: scene.text,
      voiceId: settings.voiceId,
      apiKey: settings.elevenLabsApiKey,
      stability: settings.voiceStability,
      similarityBoost: settings.voiceSimilarityBoost,
      signal,
    };

    const result = await generateAudio(params);
    const audioUrl = URL.createObjectURL(result.blob);

    logger.info('個別音声生成成功', {
      sceneId: scene.id,
      duration: result.duration,
    });

    return {
      sceneId: scene.id,
      audioUrl,
      audioDuration: result.duration,
    };
  }

  async generateBatchAudio(
    scenes: Scene[],
    settings: AppSettings,
    onProgress?: ProgressCallback
  ): Promise<AudioGenerationResult[]> {
    const results: AudioGenerationResult[] = [];
    let completedCount = 0;

    for (let i = 0; i < scenes.length; i += AUDIO_GENERATION.CONCURRENT_LIMIT) {
      const batch = scenes.slice(i, i + AUDIO_GENERATION.CONCURRENT_LIMIT);
      onProgress?.(completedCount, scenes.length);

      try {
        const batchResults = await Promise.allSettled(
          batch.map((scene) => this.generateSingleAudio(scene, settings))
        );

        for (const result of batchResults) {
          if (result.status === 'rejected') {
            if (result.reason instanceof RateLimitError) {
              logger.error('レート制限エラーが発生しました', result.reason);
              throw result.reason;
            }
            throw result.reason;
          }
          results.push(result.value);
          completedCount++;
        }
      } catch (error) {
        if (error instanceof RateLimitError) {
          throw error;
        }
        throw error;
      }
    }

    logger.info('バッチ音声生成完了', { totalCount: results.length });
    return results;
  }

  async uploadAudioFile(file: File): Promise<AudioGenerationResult> {
    logger.info('音声ファイルアップロード開始', {
      fileName: file.name,
      fileSize: file.size,
    });

    const blob = new Blob([file], { type: file.type });
    const duration = await getAudioDuration(blob);
    const audioUrl = URL.createObjectURL(blob);

    logger.info('音声ファイルアップロード成功', { duration });

    return {
      sceneId: '',
      audioUrl,
      audioDuration: duration,
    };
  }

  filterScenesForGeneration(scenes: Scene[]): Scene[] {
    return scenes.filter((s) => s.text && s.audioStatus !== 'done');
  }

  downloadAudio(audioUrl: string, filename: string): void {
    const a = document.createElement('a');
    a.href = audioUrl;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async downloadAllAudios(scenes: Scene[]): Promise<void> {
    const audioScenes = scenes.filter(
      (s) => s.audioStatus === 'done' && s.audioUrl
    );

    if (audioScenes.length === 0) {
      throw new Error('ダウンロードする音声がありません');
    }

    for (const scene of audioScenes) {
      this.downloadAudio(scene.audioUrl!, `scene_${scene.order + 1}_audio.mp3`);
      // ブラウザの並行ダウンロード制限を回避するため遅延を追加
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  async generateBatchAudioWithCallbacks(
    scenes: Scene[],
    settings: AppSettings,
    callbacks: BatchGenerationCallbacks,
    signal?: AbortSignal
  ): Promise<BatchGenerationResult> {
    let completedCount = 0;
    const totalScenes = scenes.length;

    for (let i = 0; i < totalScenes; i += AUDIO_GENERATION.CONCURRENT_LIMIT) {
      // キャンセルチェック
      if (signal?.aborted) {
        logger.info('音声生成がキャンセルされました', { completedCount });
        return { success: false, completedCount };
      }

      const batch = scenes.slice(i, i + AUDIO_GENERATION.CONCURRENT_LIMIT);
      const batchEndIndex = Math.min(i + batch.length, totalScenes);

      callbacks.onProgress(
        completedCount + 1,
        totalScenes,
        `シーン${completedCount + 1}-${batchEndIndex}/${totalScenes}の音声を生成中...`
      );

      // バッチ内の各シーンの生成開始を通知
      batch.forEach((scene) => callbacks.onSceneStart(scene.id));

      const results = await Promise.allSettled(
        batch.map((scene) => this.generateSingleAudio(scene, settings, signal))
      );

      for (let j = 0; j < results.length; j++) {
        const result = results[j];
        const scene = batch[j];

        if (result.status === 'rejected') {
          const error = result.reason as Error;

          // AbortErrorの場合はキャンセルとして処理
          if (isAbortError(error)) {
            logger.info('音声生成がキャンセルされました', { completedCount });
            return { success: false, completedCount };
          }

          logger.error('音声生成失敗', error, { sceneId: scene.id });

          const shouldContinue = await callbacks.onError(error, scene.id);
          if (!shouldContinue) {
            return { success: false, completedCount };
          }
        } else {
          callbacks.onSceneComplete(scene.id, {
            audioUrl: result.value.audioUrl,
            audioDuration: result.value.audioDuration,
          });
          completedCount++;

          // 各シーン完了時に進捗を更新
          callbacks.onProgress(
            completedCount,
            totalScenes,
            `シーン${completedCount}/${totalScenes}の音声生成完了`
          );
        }
      }
    }

    logger.info('バッチ音声生成完了（コールバック版）', { totalCount: completedCount });
    return { success: true, completedCount };
  }

  /**
   * Web Workerを使用したバッチ音声生成（バックグラウンドタブでも動作）
   */
  async generateBatchAudioWithWorker(
    scenes: Scene[],
    settings: AppSettings,
    callbacks: BatchGenerationCallbacks
  ): Promise<BatchGenerationResult> {
    let completedCount = 0;
    const totalScenes = scenes.length;

    logger.info('Worker版バッチ音声生成開始', { totalScenes });

    for (let i = 0; i < totalScenes; i += AUDIO_GENERATION.CONCURRENT_LIMIT) {
      const batch = scenes.slice(i, i + AUDIO_GENERATION.CONCURRENT_LIMIT);
      const batchEndIndex = Math.min(i + batch.length, totalScenes);

      callbacks.onProgress(
        completedCount + 1,
        totalScenes,
        `シーン${completedCount + 1}-${batchEndIndex}/${totalScenes}の音声を生成中...`
      );

      // バッチ内の各シーンの生成開始を通知
      batch.forEach((scene) => callbacks.onSceneStart(scene.id));

      const results = await Promise.allSettled(
        batch.map((scene) => this.generateSingleAudioWithWorker(scene, settings))
      );

      for (let j = 0; j < results.length; j++) {
        const result = results[j];
        const scene = batch[j];

        if (result.status === 'rejected') {
          const error = result.reason as Error;

          // AbortErrorの場合はキャンセルとして処理
          if (isAbortError(error)) {
            logger.info('Worker版音声生成がキャンセルされました', { completedCount });
            return { success: false, completedCount };
          }

          logger.error('Worker版音声生成失敗', error, { sceneId: scene.id });

          const shouldContinue = await callbacks.onError(error, scene.id);
          if (!shouldContinue) {
            return { success: false, completedCount };
          }
        } else {
          callbacks.onSceneComplete(scene.id, {
            audioUrl: result.value.audioUrl,
            audioDuration: result.value.audioDuration,
          });
          completedCount++;

          // 各シーン完了時に進捗を更新
          callbacks.onProgress(
            completedCount,
            totalScenes,
            `シーン${completedCount}/${totalScenes}の音声生成完了`
          );
        }
      }
    }

    logger.info('Worker版バッチ音声生成完了', { totalCount: completedCount });
    return { success: true, completedCount };
  }
}
